export const JACKPOT_MILESTONES = [
  50_000_000,
  100_000_000,
  250_000_000,
  500_000_000,
  750_000_000,
  1_000_000_000
];

const DAY_MS = 24 * 60 * 60 * 1000;
const EASTERN_WEEKDAY = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  weekday: 'short'
});
const PACIFIC_DATE = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  month: 'short',
  day: 'numeric'
});
const WEEKDAY_INDEX = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6
};

const GAME_MODELS = {
  powerball: {
    drawWeekdays: [1, 3, 6],
    baseGrowth: 6_000_000,
    growthRate: 0.05,
    minGrowth: 8_000_000,
    maxGrowth: 150_000_000
  },
  'mega-millions': {
    drawWeekdays: [2, 5],
    baseGrowth: 12_000_000,
    growthRate: 0.025,
    minGrowth: 12_000_000,
    maxGrowth: 125_000_000
  }
};

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum);
}

function validDate(value) {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function weekdayInEastern(timestamp) {
  return WEEKDAY_INDEX[EASTERN_WEEKDAY.format(new Date(timestamp))];
}

function nextScheduledDraw(timestamp, model) {
  let candidate = Date.parse(timestamp);
  for (let day = 1; day <= 7; day += 1) {
    candidate += DAY_MS;
    if (model.drawWeekdays.includes(weekdayInEastern(candidate))) {
      return new Date(candidate).toISOString();
    }
  }
  return null;
}

function scheduledDrawsBetween(start, end, model) {
  const endTimestamp = Date.parse(end);
  let cursor = start;
  for (let count = 1; count <= 12; count += 1) {
    cursor = nextScheduledDraw(cursor, model);
    if (!cursor) {
      return null;
    }
    if (Date.parse(cursor) >= endTimestamp - 6 * 60 * 60 * 1000) {
      return count;
    }
  }
  return null;
}

function fallbackGrowth(amount, model) {
  return clamp(
    model.baseGrowth + amount * model.growthRate,
    model.minGrowth,
    model.maxGrowth
  );
}

function weightedMedian(values) {
  const sorted = [...values].sort((left, right) => left.value - right.value);
  const totalWeight = sorted.reduce((total, item) => total + item.weight, 0);
  let accumulated = 0;
  for (const item of sorted) {
    accumulated += item.weight;
    if (accumulated >= totalWeight / 2) {
      return item.value;
    }
  }
  return sorted.at(-1)?.value ?? 1;
}

function distinctSnapshots(history) {
  const byDrawing = new Map();
  for (const row of Array.isArray(history) ? history : []) {
    if (!Number.isFinite(row?.estimatedJackpot) || !validDate(row?.nextDrawAt)) {
      continue;
    }
    const existing = byDrawing.get(row.nextDrawAt);
    if (
      !existing ||
      Date.parse(row.fetchedAt ?? '') > Date.parse(existing.fetchedAt ?? '')
    ) {
      byDrawing.set(row.nextDrawAt, row);
    }
  }
  return [...byDrawing.values()].sort(
    (left, right) => Date.parse(left.nextDrawAt) - Date.parse(right.nextDrawAt)
  );
}

function rolloverSamples(history, model) {
  const snapshots = distinctSnapshots(history);
  const samples = [];
  for (let index = 1; index < snapshots.length; index += 1) {
    const previous = snapshots[index - 1];
    const current = snapshots[index];
    const increase = current.estimatedJackpot - previous.estimatedJackpot;
    const drawCount = scheduledDrawsBetween(
      previous.nextDrawAt,
      current.nextDrawAt,
      model
    );
    if (increase <= 0 || !drawCount) {
      continue;
    }
    const growthPerDraw = increase / drawCount;
    const defaultAtSample = fallbackGrowth(previous.estimatedJackpot, model);
    const ratio = growthPerDraw / defaultAtSample;
    if (
      growthPerDraw < 2_000_000 ||
      growthPerDraw > 200_000_000 ||
      ratio < 0.25 ||
      ratio > 4
    ) {
      continue;
    }
    samples.push({
      amount: previous.estimatedJackpot,
      ratio,
      nextDrawAt: current.nextDrawAt
    });
  }
  return samples.slice(-16);
}

function calibrationRatio(amount, samples) {
  if (samples.length === 0) {
    return 1;
  }
  return weightedMedian(samples.map((sample, index) => {
    const age = samples.length - index - 1;
    const similarity = 1 / (
      1 + 2 * Math.abs(Math.log((sample.amount + 1) / (amount + 1)))
    );
    return {
      value: sample.ratio,
      weight: (0.88 ** age) * similarity
    };
  }));
}

function growthPredictor(model, samples) {
  const historyBlend = Math.min(0.8, samples.length * 0.08);
  return (amount, speed = 1) => {
    const ratio = calibrationRatio(amount, samples);
    const calibratedRatio = 1 + historyBlend * (ratio - 1);
    const prediction = fallbackGrowth(amount, model) * calibratedRatio * speed;
    return Math.round(
      clamp(prediction, model.minGrowth, model.maxGrowth) / 1_000_000
    ) * 1_000_000;
  };
}

function forecastUncertainty(samples) {
  if (samples.length === 0) {
    return 0.35;
  }
  const median = weightedMedian(samples.map((sample) => ({
    value: sample.ratio,
    weight: 1
  })));
  const deviation = weightedMedian(samples.map((sample) => ({
    value: Math.abs(sample.ratio - median),
    weight: 1
  })));
  const sampleFloor = samples.length < 4 ? 0.3 : 0.18;
  return clamp(Math.max(sampleFloor, 0.18 + deviation * 0.5), 0.18, 0.45);
}

function simulateMilestone(
  currentJackpot,
  nextDrawAt,
  target,
  model,
  predictGrowth,
  speed
) {
  let amount = currentJackpot;
  let drawAt = nextDrawAt;
  for (let drawings = 1; drawings <= 200; drawings += 1) {
    amount += predictGrowth(amount, speed);
    drawAt = nextScheduledDraw(drawAt, model);
    if (!drawAt) {
      return null;
    }
    if (amount >= target) {
      return { drawAt, drawings, projectedJackpot: amount };
    }
  }
  return null;
}

export function forecastJackpotMilestones({
  game,
  currentJackpot,
  nextDrawAt,
  history = [],
  milestones = JACKPOT_MILESTONES
}) {
  const model = GAME_MODELS[game];
  if (!model || !Number.isFinite(currentJackpot) || !validDate(nextDrawAt)) {
    return {
      confidence: 'unavailable',
      basis: 'unavailable',
      sampleCount: 0,
      milestones: milestones.map((target) => ({ target, status: 'unavailable' }))
    };
  }

  const samples = rolloverSamples(history, model);
  const predictGrowth = growthPredictor(model, samples);
  const uncertainty = forecastUncertainty(samples);
  const confidence = samples.length >= 10
    ? 'high'
    : samples.length >= 4
      ? 'medium'
      : 'low';
  const basis = samples.length === 0
    ? 'baseline'
    : samples.length >= 10
      ? 'observed'
      : 'blended';

  return {
    confidence,
    basis,
    sampleCount: samples.length,
    uncertainty,
    milestones: milestones.map((target) => {
      if (currentJackpot >= target) {
        return { target, status: 'reached', drawAt: nextDrawAt, drawings: 0 };
      }
      const expected = simulateMilestone(
        currentJackpot,
        nextDrawAt,
        target,
        model,
        predictGrowth,
        1
      );
      const earliest = simulateMilestone(
        currentJackpot,
        nextDrawAt,
        target,
        model,
        predictGrowth,
        1 + uncertainty
      );
      const latest = simulateMilestone(
        currentJackpot,
        nextDrawAt,
        target,
        model,
        predictGrowth,
        1 - uncertainty
      );
      if (!expected || !earliest || !latest) {
        return { target, status: 'unavailable' };
      }
      return {
        target,
        status: 'estimated',
        drawAt: expected.drawAt,
        earliestDrawAt: earliest.drawAt,
        latestDrawAt: latest.drawAt,
        drawings: expected.drawings,
        projectedJackpot: expected.projectedJackpot
      };
    })
  };
}

export function formatForecastWindow(milestone) {
  if (milestone.status === 'reached') {
    return 'we made it';
  }
  if (
    milestone.status !== 'estimated' ||
    !validDate(milestone.earliestDrawAt) ||
    !validDate(milestone.latestDrawAt)
  ) {
    return 'not enough data';
  }
  const earliest = PACIFIC_DATE.format(new Date(milestone.earliestDrawAt));
  const latest = PACIFIC_DATE.format(new Date(milestone.latestDrawAt));
  return earliest === latest ? earliest : `${earliest} – ${latest}`;
}
